// Shared notifications bridge.
//
// NotificationWrapper (self-loading, primes `root.notifs`) is now defined
// HERE, locally — relocated from `@symbo-ls/workspace-shell/functions/
// notifications/collab/{index,components}.js` (opus lane, 2026-08-19,
// tickets/opus.md census H2). It used to be a cross-package re-export; that
// re-export NEVER resolved for any consumer that declares `shared` without
// also declaring `workspace-shell` (docs, marketplace, analytics) — a
// plain top-level `export {X} from '@symbo-ls/<lib>/...'` barrel is stubbed
// to `""` by frank regardless of declaration (FRANK-PAGES-BAG-1,
// tickets/done.md — the declared-library resolver only rewrites free-var
// reads inside serialized FUNCTION BODIES, never a top-level module
// binding, which frank resolves by evaluating the esbuild bundle in Node at
// push time, where every `@symbo-ls/*` specifier is unconditionally
// externalized+stubbed). `AppHeader.js` (below, this same package)
// bare-keys `NotificationWrapper` — every app merging `shared` depends on
// this resolving to a real component, so "declare the shell" could never
// have fixed it; only owning the definition locally does.
//
// CANONICAL COPY. `workspace-shell`'s own
// `functions/notifications/collab/components.js` keeps a DELIBERATE, NOT
// accidental, local duplicate for its own boot — see that file's header for
// why (it is project-local there, which already wins over any shared-
// library merge for the shell's own render tree, and redirecting it to
// import `@symbo-ls/workspace-shared` here would add a NEW cross-package
// edge on the shell's own `smbls push`, itself frank-published despite
// `bundler: parcel`, risking the identical stub trap this relocation just
// fixed — see tickets/opus.md for the full reasoning). Keep the two bodies
// in sync by hand if either changes; this one is the one every OTHER
// consumer (canvas, marketplace, docs, analytics, preview) actually gets.
//
// Reads via `../functions/notifications/listNotifications.js` — a local,
// self-contained copy of the same query (own `./sdk.js` getSDK, no
// cross-package import), replacing the old relative `import('./queries.js')`
// into workspace-shell's own collab/ folder.
//
// NotificationPanel is the SOLE copy (duplicate-name collision fix,
// 2026-07-27) and since 2026-07-31 it is a DROPDOWN-LAYER CONTENT component:
// the bell opens it via `el.call('openDropdown', { content: { extends:
// 'NotificationPanel' } })`, so it renders inside the shell-root
// DropdownRoot (position:fixed, viewport coords, glass theme, ESC +
// outside-click owned by the plugin). It previously mounted absolutely
// INSIDE the header with its own zIndex:3000 + a hand-rolled mousedown
// closer — trapped in the header's stacking context, painted under bespoke
// page surfaces (which climb to 10001), and duplicated dismissal logic the
// layer already owns. Layout-only now: no position, no zIndex, no display
// gate, no document listeners.
//
// It lives HERE, not in workspace/components, because SHARED is the layer
// every app scope merges (canvas's standalone bundle links shared, never the
// workspace package) — one canonical panel everywhere, and its
// el.call('markNotificationRead' | 'markAllNotificationsRead') resolve from
// shared/functions too.
//
// NotificationBell is owned by ./NotificationBell.js (this bridge does not
// re-export it, so frank's duplicate-export scan doesn't flag both files).
import { listNotifications } from '../functions/notifications/listNotifications.js'

// Pure ESM import (never el.call) — same convention as openNotificationTarget's
// own workspacePrefix import, and it keeps the panel usable in canvas's
// standalone bundle where the workspace-shell function registry isn't merged.
import { filterNotifications, isNotifFilterActive } from '../functions/notificationScope.js'

export const NotificationWrapper = {
  // FA204 / frank trap (project_frank_drops_module_local_helpers) — the
  // load helper lives ON `scope`, not as a bare module-scope const, exactly
  // like NotificationRow's/NotificationPanel's own `scope` blocks below in
  // this file: a bare module-scope function referenced only from inside a
  // serialized factory (`onInit`/`onUpdate` are factory-shaped values too)
  // reads as undefined once frank.toJSON's this component for platform
  // delivery. `el.scope.foo(...)` binds `this` to `el.scope`, same
  // convention as NotificationPanel's `unread()` calling `this.all(...)`.
  //
  // Loads root.notifs once a real recipient email is known, and re-loads if
  // it changes (sign-out/sign-in in the same tab). Guarding on
  // `el._loadedFor === email` (not a bare "already ran" boolean) mirrors the
  // fix verified live 2026-07-30 against the PersonPopover Ping action in
  // the original workspace-shell copy this was relocated from: the previous
  // guard set `el._loaded = true` UNCONDITIONALLY before checking
  // `currentUserEmail`, so if this component's first (and, absent a
  // subscription, likely ONLY) onRender fired before auth hydration
  // finished — routine on a fresh boot — it saw an empty email, bailed, and
  // never got another chance.
  scope: {
    loadNotifs(el, s) {
      const email = s.root?.currentUserEmail
      if (!email || el._loadedFor === email) return
      el._loadedFor = email
      ;(async () => {
        const { data } = await listNotifications()
        const rows = (data || []).map((n) => ({
          ...n,
          createdAt: n.created_at,
          read: !!n.read
        }))
        s.root.update({ notifs: rows })
      })()
    }
  },

  position: 'relative',
  subscribeTo: ['currentUserEmail'],

  onInit: (el, s) => el.scope.loadNotifs(el, s),
  onUpdate: (el, s) => el.scope.loadNotifs(el, s),

  // Panel is dropdown-layer content now (the bell opens it via
  // openDropdown) — mounting it here would render a second, in-flow copy.
  NotificationBell: {}
}

// One notification row — shared by the bell dropdown panel's List and the
// full /inbox page (extracted 2026-08-01 when /inbox landed; keep the two
// surfaces byte-identical by editing HERE only).
// v3.14: consumed via childrenAs:'state', so the row's state IS the
// notification — read fields FLAT (s.title, s.type, s.read), never the dead
// v3 `s.parent.state.X` (which silently returned undefined and blanked rows).
//
// White-label redesign (DESIGN_SPEC §9, P7 — 2026-09-26): the row is FLAT and
// a real <button>. Unread is carried by the one blue dot and the heavier
// title, never by a wash under the whole row (the wash read as a selection
// and stacked with the hover). The type glyph sits on the neutral widget
// plate in `caption` ink — hue only as dots (the per-type tints painted
// green/purple glyphs on 15% washes of themselves). As a button the row is
// reachable by Tab and activates on Enter/Space; its inner parts are spans
// (phrasing content inside a button).
export const NotificationRow = {
  // FA204 / frank trap — ICON_BY_TYPE/timeAgo used to be bare module-scope
  // const/function read from inside nested factories (`name`, `text`).
  // Those read as undefined once this component was frank.toJSON'd for
  // platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.<name>`.
  scope: {
    ICON_BY_TYPE: {
      mention: 'chat',
      ticket_assign: 'check',
      calendar_invite: 'date',
      doc_share: 'document',
      system: 'settings',
      // The comms-phone pipeline (a WhatsApp / SMS message, a missed call)
      channel_message: 'chat',
      channel_missed_call: 'phone'
    },

    // Compact format ("now" / "5m" / "3h" / "2d" / "1w") for the tight meta
    // column of a notification row — intentionally distinct from the verbose
    // house `el.call('timeAgo', ts)` ("5 mins ago") used on ticket cards.
    //
    // `el` first: the unit letter is a WORD too. `3d` read English on a
    // Georgian inbox (live walk 2026-09-21); the bundle carries the compact
    // form per language (`common.timeShort.*`, `{count} დღე`). A language with no
    // such key keeps the English letter, never a key string.
    timeAgo(el, iso) {
      if (!iso) return ''
      const short = (key, count, fallback) => {
        const text = el && el.call ? el.call('polyglot', key) : null
        return text && text !== key ? text.replace('{count}', count) : fallback
      }
      const ms = Date.now() - new Date(iso).getTime()
      const mins = Math.floor(ms / 60000)
      if (mins < 1) return short('common.timeShort.now', '', 'now')
      if (mins < 60) return short('common.timeShort.minutes', mins, `${mins}m`)
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return short('common.timeShort.hours', hrs, `${hrs}h`)
      const days = Math.floor(hrs / 24)
      if (days < 7) return short('common.timeShort.days', days, `${days}d`)
      const weeks = Math.floor(days / 7)
      return short('common.timeShort.weeks', weeks, `${weeks}w`)
    },

    notificationBody(el, notification) {
      const body = el.call('htmlToPlainText', notification?.body) || ''
      const metadata = notification?.metadata
      if (metadata?.kind !== 'meeting_starting' || !metadata.startAt) return body
      const start = new Date(metadata.startAt)
      if (!Number.isFinite(start.getTime())) return body
      const localTime = el.call('localizedDate', start, {
        hour: 'numeric',
        minute: '2-digit'
      })
      if (!localTime) return body
      // The scheduler's prose currently includes a server-formatted time.
      // Keep the copy, but replace that clock with the canonical instant in
      // the viewer's locale/timezone.
      return body.replace(/(\bat\s+)\d{1,2}:\d{2}(?:\s*[AP]M)?/i, `$1${localTime}`)
    }
  },

  tag: 'button',
  type: 'button',
  width: '100%',
  flow: 'x',
  gap: 'Z1',
  align: 'flex-start',
  padding: 'Z1 Z',
  round: 'radiusControl',
  borderWidth: '0',
  background: 'transparent',
  color: 'title',
  textAlign: 'left',
  cursor: 'pointer',
  position: 'relative',
  transition: 'background-color A defaultBezier',
  ':hover': { background: 'hover' },
  ':active': { background: 'selected' },
  // The lists that hold this row scroll (overflow: auto), where the house
  // ring's outer spread is clipped — the inset variant, as MenuPanelItem.
  ':focus-visible': { shadow: 'focusRingInset' },

  onClick: (e, el, s) => {
    e.stopPropagation()
    el.call('markNotificationRead', s.id)
    // The row's own href, else the one its type implies (a channel message or
    // missed call → the sender's CRM contact, or the CRM inbox) —
    // functions/notificationHrefOf.js, shared with the toast.
    const href = el.call('notificationHrefOf', { href: s.href, type: s.type, metadata: s.metadata })
    if (href) {
      // No-op outside a dropdown layer (the /inbox page) — the plugin's
      // close is idempotent when nothing is open.
      el.call('closeDropdown')
      // Feature-modal first: a ticket/event notification opens its detail
      // modal IN PLACE (TicketsDetailModal / CalEventDetailModal — current
      // route preserved), anything else keeps the shared slot-vs-navigate
      // decision (openNotificationTarget.js owns the mapping; it hands
      // non-modal targets to openInSlot — shared with the toast click).
      el.call('openNotificationTarget', { href, type: s.type, metadata: s.metadata }, el)
    }
  },

  // The type glyph on the neutral plate.
  IconWrap: {
    tag: 'span',
    display: 'flex',
    width: 'B1',
    height: 'B1',
    minWidth: 'B1',
    round: 'radiusPill',
    align: 'center center',
    flexShrink: '0',
    background: 'widgetPlate',
    color: 'caption',
    Icon: {
      name: (el, s) => el.scope.ICON_BY_TYPE[s.type] || 'star',
      boxSize: 'A'
    }
  },

  Body: {
    tag: 'span',
    display: 'flex',
    flex: '1',
    flow: 'y',
    gap: 'X',
    minWidth: '0',

    // PRIMARY text: never cut (overflow law, HOME_AMBIENT_SKIN_SPEC §3b
    // rule 8) — it wraps, and a long unbroken word (an email, a URL, a
    // Georgian compound) breaks anywhere rather than pushing the time out.
    NTitle: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: (el, s) => (s.read ? '500' : '700'),
      color: 'title',
      minWidth: '0',
      overflowWrap: 'anywhere',
      text: (el, s) => s.title || ''
    },

    NBody: {
      // PROSE, never markup. A calendar invitation carries the event
      // description, and a Google Calendar description is HTML — the row
      // printed `<p>Global Tech Weekend…` with its tags (measured live). Still
      // `text:`, so nothing is ever parsed; the helper only reads it as a line.
      // A body that is ONLY markup mounts no empty line.
      if: (el, s) => !!el.scope.notificationBody(el, s),
      // Secondary meta: one line, and the whole text on hover (rule 8 — an
      // ellipsis only where the full text rides in `title`).
      tag: 'span',
      fontSize: 'Z',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      title: (el, s) => el.scope.notificationBody(el, s),
      text: (el, s) => el.scope.notificationBody(el, s)
    }
  },

  Meta: {
    tag: 'span',
    display: 'flex',
    flow: 'y',
    align: 'center flex-end',
    gap: 'V',
    flexShrink: '0',

    Time: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      text: (el, s) => el.scope.timeAgo(el, s.createdAt || s.created_at)
    },

    // Attention is the accent (P7): the one hue on the row, as a dot.
    UnreadDot: {
      tag: 'span',
      display: 'block',
      hide: (el, s) => !!s.read,
      width: 'Z',
      height: 'Z',
      round: 'radiusPill',
      background: 'blue',
      flexShrink: '0'
    }
  }
}

// The header bell's popover — dropdown-layer CONTENT (the bell opens it with
// `el.call('openDropdown', { content: { extends: 'NotificationPanel' } })`;
// the plugin owns the anchoring, the glass paint the bell asks for, Esc,
// outside-click and focus return to the bell).
//
// White-label redesign (DESIGN_SPEC §9 "Notifications popover", 2026-09-26):
//   • a labelled DIALOG — its heading names it, and focus moves in on open
//     (to the first notification, else the panel itself);
//   • the head reads "Notifications 8": the unread count on the title's line
//     in the mono face and the quiet `inactive` ink — the blue count pill is
//     gone (hue is for the unread dot alone, P7); "Mark all read" is a ghost
//     capsule; the one Close is the house CloseButton;
//   • All · Unread · Workspace is the shared AppSegmented at VIEW strength
//     (the raised thumb) — it switches what the list shows. The row
//     contract's `select` would need a new shared function, so the track uses
//     the documented options contract with this panel's own wiring through
//     `childProps` (the filter lives on the root, `_notifFilter`);
//   • rows are flat (NotificationRow above); the empty list is the shared
//     EmptyState poster; "View all" is a ghost capsule into the /inbox window.
export const NotificationPanel = {
  // FA204 / frank trap — all/unread/filtered used to be bare module-scope
  // const/function read from inside nested factories (`hide`, `text`,
  // `children`). Those read as undefined once this component was
  // frank.toJSON'd for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.<name>`; `unread` calls the sibling `all` via `this.all(...)`
  // (el.scope.foo() binds `this` to el.scope — object-literal properties
  // can't see each other as bare identifiers otherwise). Not duplicated from
  // NotificationRow's scope above — each top-level export's `scope` only
  // resolves within its own tree, and NotificationRow doesn't need these.
  scope: {
    // The notification-center list, on its OWN key `root.notifs` (persisted
    // rows {id,type,title,body,href,read,createdAt}). The transient toast
    // queue keeps the shared `root.notifications` key (GlobalNotification) —
    // separate keys stop center rows from rendering as toasts or inflating
    // the bell count.
    all(root) {
      return root?.notifs || []
    },
    unread(root) {
      return this.all(root).filter((n) => !n.read).length
    },
    // All / Unread / Workspace share ONE routing rule with the /inbox page —
    // both surfaces are meant to stay byte-identical (see NotificationRow's
    // header) and each previously carried its own copy of this.
    filtered(root) {
      return filterNotifications(root, root?._notifFilter)
    },
    // Focus moves INTO the popover on open (DESIGN_SPEC §6.6): the first
    // notification, else the panel itself. After a frame — the dropdown
    // plugin positions the panel on the next frame, and `preventScroll`
    // keeps the focus from scrolling anything mid-entrance. Once per mount.
    focusIn(el) {
      if (el._focusedIn || !el.node || typeof el.node.focus !== 'function') return
      el._focusedIn = true
      const run = () => {
        if (!el.node || !el.node.isConnected) return
        const list = el.List
        const rows = list && typeof list.getChildren === 'function' ? list.getChildren() : []
        const first = rows && rows[0] && rows[0].node
        const target = first && first.offsetParent !== null ? first : el.node
        try {
          target.focus({ preventScroll: true })
        } catch (_) {
          target.focus()
        }
      }
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(run)
      else run()
    }
  },

  role: 'dialog',
  ariaLabelledby: 'shellNotificationsTitle',
  // Focusable as the fallback target (an empty list); never in the Tab order.
  tabindex: '-1',
  // A FLEX COLUMN THAT CLIPS NOTHING (the overflow law, HOME_AMBIENT_SKIN_SPEC
  // §3b; live on dev 2c2b6d978, Georgian): the header and the filter stay,
  // the List is the one scroll container and gives way, so the panel never
  // outgrows the dropdown's own 72vh and the "View all" foot is never cut.
  // It was `overflow: hidden` round a 56vh list — on a 757px window the foot
  // fell outside — and at `overlaySm` (400) the Georgian filter's third
  // segment ("სამუშაო სივრცე") was clipped at the panel edge. `overlayMd`
  // (480) holds all three in both languages.
  flow: 'y',
  width: 'overlayMd',
  maxWidth: '92vw',
  maxHeight: '72dvh',
  minHeight: '0',
  padding: '0',
  onRender: (el) => el.scope.focusIn(el),

  Header: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    padding: 'A A Z1 A',
    flexShrink: '0',

    // The title and the unread count on one line, one size (r077 r086). It
    // takes the row's free width and may wrap (never ellipsis, never over
    // the actions): in Georgian the nowrap title ran 110px over the old
    // text "Mark all as read" capsule (live on dev 2c2b6d978).
    TitleGroup: {
      flow: 'x',
      align: 'baseline flex-start',
      gap: 'Y',
      flex: '1 1 auto',
      minWidth: '0',

      Title: {
        tag: 'h2',
        id: 'shellNotificationsTitle',
        margin: '0',
        text: '{{ shell.notifications.panel.title | polyglot }}',
        fontWeight: '700',
        fontSize: 'B',
        lineHeight: '1.25',
        color: 'title',
        minWidth: '0',
        overflowWrap: 'anywhere'
      },

      Count: {
        hide: (el, s) => el.scope.unread(s.root) === 0,
        tag: 'span',
        fontFamily: 'Code',
        fontSize: 'B',
        fontWeight: '500',
        lineHeight: '1.25',
        fontVariantNumeric: 'tabular-nums',
        color: 'inactive',
        text: (el, s) => String(el.scope.unread(s.root))
      }
    },

    Actions: {
      flow: 'x',
      align: 'center',
      gap: 'Y',
      flexShrink: '0',

      // Lives in the HEADER, not on the filter track: /inbox keeps its own in
      // its header too, so the two surfaces agree. In this 480px popover it
      // is the icon beside the close X — a square on the compact control box
      // (all icon buttons are 1/1, Nika 2026-09-26), named by its tooltip and
      // its accessible name. As a text capsule its Georgian label
      // ("ყველა წაკითხულად მონიშვნა") left the title no room (live on dev
      // 2c2b6d978); the /inbox page keeps the text form in its wide header.
      //
      // On the kit's icon-only square (CompactIconButton — the brand icon
      // law restated at the call site until the brand publish lands:
      // controlHCompact, aspect 1 / 1, padding 0, flex-shrink 0, border-box),
      // beside the brand CloseButton it stands next to.
      MarkAllBtn: {
        extends: ['CompactIconButton', 'TitleTooltipTrigger'],
        icon: 'checkmark',
        type: 'button',
        theme: 'transparent',
        background: 'transparent',
        color: 'caption',
        ':hover': { background: 'hover', color: 'title' },
        title: (el) => el.call('polyglot', 'shell.notifications.markAllRead'),
        hide: (el, s) => el.scope.unread(s.root) === 0,
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('markAllNotificationsRead')
        }
      },

      // THE house close X (brand CloseButton — F2, one X for every panel).
      CloseButton: {
        extends: 'CloseButton',
        title: (el) => el.call('polyglot', 'shell.notifications.close'),
        aria: { label: (el) => el.call('polyglot', 'shell.notifications.close') },
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('closeDropdown')
          s.root.update({ notificationsPanelOpen: false })
        }
      }
    }
  },

  // All / Unread / Workspace — the shared segmented control at VIEW strength.
  // The filter is the root's `_notifFilter` (declared in state, default
  // 'all'); `isNotifFilterActive` lights All for an unset or unknown value.
  Filter: {
    extends: 'AppSegmented',
    variant: 'view',
    margin: '0 A Z1 A',
    flexShrink: '0',
    options: [
      { value: 'all', label: 'shell.notifications.tab.all' },
      { value: 'unread', label: 'shell.notifications.tab.unread' },
      { value: 'workspace', label: 'shell.notifications.tab.workspace' }
    ],
    childProps: {
      isActive: (el, s) => isNotifFilterActive(s.root, el.optionValue),
      ariaPressed: (el, s) => (isNotifFilterActive(s.root, el.optionValue) ? 'true' : 'false'),
      // "Unread · 8" while anything is unread — the segment says what it
      // holds (the /inbox track does the same with its own bundle key).
      text: (el, s) => {
        const n = el.optionValue === 'unread' ? el.scope.unread(s.root) : 0
        return n > 0
          ? el.call('polyglot', 'shell.notifications.tab.unreadCount').replace('{count}', n)
          : el.call('polyglot', el.optionLabel || '')
      },
      title: (el) =>
        el.optionValue === 'workspace'
          ? el.call('polyglot', 'shell.notifications.tab.workspaceTooltip')
          : null,
      onClick: (e, el, s) => {
        e.stopPropagation()
        s.root.update({ _notifFilter: el.optionValue })
      }
    }
  },

  // Empty state is a SIBLING of the List slot, never a child — smbls 3.14
  // treats children:+childExtends: as the authoritative child set on a node,
  // so a named key declared inside List gets wiped by the children factory
  // every render. The shared poster: the bell on the neutral plate, one line
  // specific to the filter ("You're all caught up" would be a lie when other
  // workspaces still have rows waiting one tab away).
  Empty: {
    extends: 'EmptyState',
    hide: (el, s) => !!el.scope.filtered(s.root).length,
    icon: 'bell',
    padding: 'C A',
    heading: (el, s) => {
      const f = s.root?._notifFilter
      if (f === 'unread') return 'shell.notifications.empty.unread'
      if (f === 'workspace') return 'shell.notifications.empty.workspace'
      return 'shell.notifications.empty.all'
    }
  },

  // Rounded rows separated by breathing room + hover tint — never hairline
  // row borders inside a rounded surface (house rule).
  List: {
    hide: (el, s) => !el.scope.filtered(s.root).length,
    // THE one scroll container of the panel: it takes what the header, the
    // filter and the foot leave inside the panel's 72vh, never a height of
    // its own (it was 56vh, which the foot then fell under).
    overflow: 'auto',
    overscrollBehavior: 'contain',
    flex: '1 1 auto',
    minHeight: '0',
    flow: 'y',
    gap: 'X',
    padding: '0 Z Z Z',

    childrenAs: 'state',
    children: (el, s) => el.scope.filtered(s.root).slice(0, 40),
    childExtends: 'NotificationRow'
  },

  // Into the full inbox — a ghost capsule, centred; no separator line (gap
  // does the work).
  Footer: {
    extends: 'AppCapsuleBtnGhost',
    compact: true,
    alignSelf: 'center',
    flexShrink: '0',
    margin: 'X 0 Z',
    text: '{{ shell.notifications.viewAll | polyglot }}',
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('closeDropdown')
      // Slot-first (2026-08-06 per Nika "notifications should open in
      // pageslot") — mounts /inbox in the shell PageSlot over the current
      // route; consumers without an /inbox slot def (canvas standalone,
      // where this panel still opens as a dropdown) keep the full-page
      // navigation openInSlot falls back to, routes-walk included.
      el.call('openInSlot', '/inbox', el)
    }
  }
}
